/**
 * Which build this is, and what it may do.
 *
 * One codebase, two editions. The demo is the web version at tuner.t0bi.net:
 * it tunes a guitar in E standard at concert pitch, which is the hard part and
 * the part worth showing for free. Everything a player *chooses* — the other
 * eight tunings, a reference pitch other than 440, note naming, transposition,
 * the plucked reference tones — belongs to the paid apps.
 *
 * The line is drawn here rather than sprinkled through the components, and the
 * store enforces it rather than the UI: a control that is merely hidden is
 * still reachable, and a demo that pretends to accept a setting and quietly
 * ignores it is worse than one that says no.
 */

/** A build of multitimbral. */
export type Edition = 'demo' | 'full'

/** What an edition is allowed to do. */
export interface Capabilities {
  /** All nine tunings rather than E standard alone. */
  readonly tunings: boolean
  /** A reference pitch other than 440 Hz. */
  readonly concertPitch: boolean
  /** German and Solfeggio note names. */
  readonly notation: boolean
  /** Reading for a transposing instrument. */
  readonly transposition: boolean
  /** The plucked reference tone on each bar. */
  readonly referenceTones: boolean
}

/** The tuning the demo is fixed to. */
export const DEMO_TUNING_ID = 'e-standard'

/** The reference pitch the demo is fixed to, in Hz. */
export const DEMO_A4 = 440

const FULL: Capabilities = {
  tunings: true,
  concertPitch: true,
  notation: true,
  transposition: true,
  referenceTones: true,
}

const DEMO: Capabilities = {
  tunings: false,
  concertPitch: false,
  notation: false,
  transposition: false,
  referenceTones: false,
}

/** What an edition may do. */
export function capabilitiesFor(edition: Edition): Capabilities {
  return edition === 'demo' ? DEMO : FULL
}

/** True when nothing is held back. */
export const isFullEdition = (edition: Edition): boolean => edition === 'full'

/**
 * The edition this bundle was built as.
 *
 * Set by `VITE_MULTITIMBRAL_EDITION` at build time; anything but an explicit
 * `demo` is the full app, so a packaged build cannot end up crippled by a
 * missing environment variable.
 */
export function buildEdition(): Edition {
  return import.meta.env.VITE_MULTITIMBRAL_EDITION === 'demo' ? 'demo' : 'full'
}
